import type { PrintBookListItem } from '@/api/printBook'

export interface PrintBookSeries {
  head: PrintBookListItem
  books: PrintBookListItem[]
  totalBookCount: number
  hasMissingLink: boolean
  missingNextBookIds: number[]
  hasCycle: boolean
  /** 다른 교재 계열로 이어지는 다음 권 (예: 코어 C 체크업북 마지막 권 → 어드밴스드 체크업북 1권). */
  continuesTo: PrintBookListItem | null
}

/**
 * 목록 묶음의 계열 키 = 유형 + 끝 번호를 뗀 라벨.
 * next_book_id 체인은 진도 알림·현황판의 "다음 권" 계산용이라 코어 → 어드밴스드 → 엘리트로 계속 이어지지만,
 * 교재 관리 목록은 계열별로 따로 보여야 한다. 코어 A1 → A2 → A3처럼 분할 코스 라벨은 한 계열로 본다.
 */
export function getPrintBookSeriesFamily(book: PrintBookListItem) {
  const label = (book.bookLabel || book.title).trim().replace(/\d+$/, '').toUpperCase()
  return `${book.materialType ?? ''}|${label}`
}

function isSameSeries(book: PrintBookListItem, nextBook: PrintBookListItem) {
  return getPrintBookSeriesFamily(book) === getPrintBookSeriesFamily(nextBook)
}

function buildSeriesFromHead(
  head: PrintBookListItem,
  booksById: Map<number, PrintBookListItem>,
  visited: Set<number>,
): PrintBookSeries {
  const books: PrintBookListItem[] = []
  const missingNextBookIds: number[] = []
  const localVisited = new Set<number>()
  let current: PrintBookListItem | undefined = head
  let hasCycle = false
  let continuesTo: PrintBookListItem | null = null

  while (current) {
    if (localVisited.has(current.id)) {
      hasCycle = true
      break
    }

    localVisited.add(current.id)
    visited.add(current.id)
    books.push(current)

    if (current.nextBookId == null) break

    const nextBook = booksById.get(current.nextBookId)
    if (!nextBook) {
      missingNextBookIds.push(current.nextBookId)
      break
    }

    if (!isSameSeries(current, nextBook)) {
      continuesTo = nextBook
      break
    }

    current = nextBook
  }

  return {
    head,
    books,
    totalBookCount: books.length,
    hasMissingLink: missingNextBookIds.length > 0,
    missingNextBookIds,
    hasCycle,
    continuesTo,
  }
}

export function buildPrintBookSeries(books: PrintBookListItem[]): PrintBookSeries[] {
  const booksById = new Map(books.map(book => [book.id, book]))
  const referencedBookIds = new Set<number>()

  books.forEach(book => {
    const nextBook = book.nextBookId == null ? undefined : booksById.get(book.nextBookId)
    if (nextBook && isSameSeries(book, nextBook)) {
      referencedBookIds.add(nextBook.id)
    }
  })

  const heads = books
    .filter(book => !referencedBookIds.has(book.id))

  const visited = new Set<number>()
  const series = heads.map(head => buildSeriesFromHead(head, booksById, visited))

  books
    .filter(book => !visited.has(book.id))
    .forEach(book => {
      series.push(buildSeriesFromHead(book, booksById, visited))
    })

  return series
}
